import { useAuth } from '@clerk/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { apiClient, bearer, unwrap } from '@/api/client';
import { getPortalChecklist } from '@/api/onboardingChecklist';
import { listPortalFiles, type CustomerFileOut } from '@/api/portalFiles';
import { getPortalIntake } from '@/api/portalIntake';
import { CustomerUploadSection } from '@/components/CustomerUploadSection';
import { OnboardingChecklist } from '@/components/OnboardingChecklist';
import { OnboardingWizard, shouldShowWizard } from '@/components/OnboardingWizard';
import { PortalIntakeCard } from '@/components/PortalIntakeCard';
import { PortalNotesCard } from '@/components/PortalNotesCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { groupByCategory, UPLOAD_CATEGORIES } from '@/lib/portalOnboarding';

/** The customer-facing onboarding view. First sign-in (questionnaire
 *  not yet submitted) walks through the one-question-per-card wizard —
 *  the only surface until submit; after that this renders the dashboard:
 *  derived checklist + stats, the intake questionnaire card,
 *  categorized upload sections, and notes. Default portal landing
 *  before a run is published. */
export function PortalOnboarding() {
  const { getToken, userId } = useAuth();

  const engagements = useQuery({
    queryKey: ['portal', 'engagements', userId],
    enabled: !!userId,
    queryFn: async () => {
      const token = (await getToken()) ?? '';
      return apiClient
        .GET('/api/portal/engagements', { headers: bearer(token) })
        .then(unwrap);
    },
  });

  if (engagements.isPending) {
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    );
  }

  const engagementId = engagements.data?.items[0]?.id ?? null;
  if (!engagementId) {
    return (
      <Card className="mx-auto max-w-2xl">
        <CardHeader>
          <CardTitle>We&apos;re getting your account ready</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-brand-slate">
          Your engagement hasn&apos;t been set up yet. Your InputRecovery contact will let you
          know as soon as you can start uploading. Check back shortly.
        </CardContent>
      </Card>
    );
  }

  return <WizardGate engagementId={engagementId} />;
}

/** Route to the walkthrough until the questionnaire is submitted —
 *  there is no dismiss path, so revisiting Documents re-enters it. The
 *  decision is made once per mount: submitting inside the wizard flips
 *  the cached intake's `submitted_at`, and re-deriving would unmount
 *  the wizard before its completion screens render. */
function WizardGate({ engagementId }: { engagementId: string }) {
  const { getToken } = useAuth();
  const [decision, setDecision] = useState<'pending' | 'wizard' | 'sections'>('pending');

  const intake = useQuery({
    queryKey: ['portal', 'intake', engagementId],
    queryFn: async () => {
      const token = (await getToken()) ?? '';
      return getPortalIntake(engagementId, token);
    },
  });

  if (decision === 'pending' && intake.data != null) {
    setDecision(shouldShowWizard(intake.data) ? 'wizard' : 'sections');
  }

  if (decision === 'pending') {
    // Fail closed: without the intake we can't know whether the
    // mandatory wizard applies, so an error never falls through to the
    // dashboard — it retries instead.
    if (intake.isError) {
      return (
        <div className="grid justify-center gap-3 py-10 text-center">
          <p className="text-sm text-destructive">Couldn&apos;t load your onboarding status.</p>
          <div>
            <Button type="button" variant="outline" onClick={() => intake.refetch()}>
              Try again
            </Button>
          </div>
        </div>
      );
    }
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    );
  }

  if (decision === 'wizard' && intake.data) {
    return (
      <OnboardingWizard
        engagementId={engagementId}
        intake={intake.data}
        onExit={() => setDecision('sections')}
      />
    );
  }

  return <OnboardingSections engagementId={engagementId} />;
}

function OnboardingSections({ engagementId }: { engagementId: string }) {
  const { getToken } = useAuth();
  const qc = useQueryClient();

  const files = useQuery({
    queryKey: ['portal', 'files', engagementId],
    queryFn: async () => {
      const token = (await getToken()) ?? '';
      return listPortalFiles(engagementId, token);
    },
  });

  const checklist = useQuery({
    queryKey: ['portal', 'checklist', engagementId],
    queryFn: async () => {
      const token = (await getToken()) ?? '';
      return getPortalChecklist(engagementId, token);
    },
  });

  const onChanged = () => {
    qc.invalidateQueries({ queryKey: ['portal', 'files', engagementId] });
    qc.invalidateQueries({ queryKey: ['portal', 'checklist', engagementId] });
  };

  const items: CustomerFileOut[] = files.data?.items ?? [];
  const byCategory = groupByCategory(items);

  return (
    <div className="mx-auto grid max-w-3xl gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-brand-navy">
          Upload your documents
        </h1>
        <p className="mt-1 text-sm text-brand-slate">
          Add your general ledger, chart of accounts, supplier invoices, prior returns, and
          anything else that supports the engagement. You can add or remove files anytime.
        </p>
      </div>

      {checklist.data && (
        <OnboardingChecklist
          data={checklist.data}
          title="Onboarding checklist"
          description="Each item checks itself off as things come in."
        />
      )}

      <PortalIntakeCard engagementId={engagementId} />

      {files.isError && (
        <p className="text-sm text-destructive">
          Couldn&apos;t load your files. Refresh to try again.
        </p>
      )}

      {UPLOAD_CATEGORIES.map((meta) => (
        <CustomerUploadSection
          key={meta.category}
          engagementId={engagementId}
          meta={meta}
          items={byCategory[meta.category]}
          onChanged={onChanged}
        />
      ))}

      <PortalNotesCard engagementId={engagementId} />
    </div>
  );
}
